import React, { useState } from 'react';
import {
  Drawer,
  Typography,
  Descriptions,
  Button,
  Space,
  Tag,
  message,
  Form,
  Input,
  Card,
  Divider,
  Alert,
  Modal,
} from 'antd';
import {
  FileTextOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  DownloadOutlined,
  UserOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import { formatDate } from '../../utils/formatters';

const { Title, Text } = Typography;
const { TextArea } = Input;

const UserRequestDetailDrawer = ({
  visible,
  request,
  onClose,
  onAction,
  loading = false,
}) => {
  const [form] = Form.useForm();

  // Get status color
  const getStatusColor = (status) => {
    const statusMap = {
      pending: 'warning',
      approved: 'success',
      declined: 'error',
    };
    return statusMap[status] || 'default';
  };

  // Get status display text
  const getStatusDisplayText = (status) => {
    const statusTextMap = {
      pending: 'Pending Review',
      approved: 'Approved',
      declined: 'Declined',
    };
    return statusTextMap[status] || status;
  };

  // Handle approve action
  const handleApprove = () => {
    Modal.confirm({
      title: 'Approve Upgrade Request',
      content: (
        <Alert
          message="Important"
          description="Approving this request will automatically upgrade the user to premium status."
          type="info"
          showIcon
        />
      ),
      onOk: () => onAction(request.id, 'approve'),
    });
  };

  // Handle decline action
  const handleDecline = () => {
    Modal.confirm({
      title: 'Decline Upgrade Request',
      content: (
        <Form form={form} layout="vertical">
          <Form.Item
            name="adminComment"
            label="Reason"
            rules={[{ required: true, message: 'Please provide a reason' }]}
          >
            <TextArea 
              rows={4} 
              placeholder="Enter reason for declining this request"
            />
          </Form.Item>
        </Form>
      ),
      onOk: async () => {
        try {
          const values = await form.validateFields();
          onAction(request.id, 'decline', values.adminComment);
        } catch (error) {
          // Form validation failed
          return Promise.reject(error);
        }
      },
    });
  };

  return (
    <Drawer
      title={
        <Space>
          <Title level={4} style={{ margin: 0 }}>
            Upgrade Request Details
          </Title>
          <Text type="secondary">Review and manage user upgrade request</Text>
        </Space>
      }
      width={800}
      open={visible}
      onClose={onClose}
      extra={
        <Space>
          <Button onClick={onClose}>Close</Button>
        </Space>
      }
    >
      {request ? (
        <div>
          {/* Request Information */}
          <Card size="small" style={{ marginBottom: 24 }}>
            <Title level={5}>Request Information</Title>
            <Descriptions column={2} size="small">
              <Descriptions.Item label="Request ID">
                <Text code>{request.id}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Type">
                <Tag color="blue">
                  {request.type === 'account-upgrade' ? 'Account Upgrade' : request.type}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color={getStatusColor(request.status)}>
                  {getStatusDisplayText(request.status)}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Created">
                {formatDate(request.created)}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Document Review Section */}
          <Card size="small" style={{ marginBottom: 24 }}>
            <Title level={5}>Required Documents</Title>
            <Text type="secondary">
              Review the utility bill provided by the user for account upgrade
            </Text>
            
            <Divider />
            
            {/* Utility Bill */}
            <Card 
              size="small" 
              style={{ marginBottom: 16 }}
              title={
                <Space>
                  <FileTextOutlined />
                  <Text strong>Utility Bill</Text>
                  <Tag color="red">Required</Tag>
                </Space>
              }
            >
              <Space>
                <Button 
                  type="link" 
                  icon={<EyeOutlined />}
                  onClick={() => window.open(request.supporting_data.utility_bill_url, '_blank')}
                >
                  View Document
                </Button>
                <Button 
                  type="link" 
                  icon={<DownloadOutlined />}
                  onClick={() => window.open(request.supporting_data.utility_bill_url, '_blank')}
                >
                  Download
                </Button>
              </Space>
            </Card>
          </Card>

          {/* Action Section - Only show for pending requests */}
          {request.status === 'pending' && (
            <Card size="small" style={{ marginBottom: 24 }}>
              <Alert
                message="Ready for Review"
                description="You can now approve or decline this upgrade request"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />
              
              <Space>
                <Button
                  type="primary"
                  size="large"
                  icon={<CheckCircleOutlined />}
                  onClick={handleApprove}
                  loading={loading}
                >
                  Approve Upgrade
                </Button>
                <Button
                  danger
                  size="large"
                  icon={<CloseCircleOutlined />}
                  onClick={handleDecline}
                  loading={loading}
                >
                  Decline Request
                </Button>
              </Space>
            </Card>
          )}

          {/* Status-specific information */}
          {request.status === 'approved' && (
            <Alert
              message="Request Approved"
              description="This upgrade request has been approved. The user's account has been upgraded to premium status."
              type="success"
              showIcon
            />
          )}

          {request.status === 'declined' && (
            <Alert
              message="Request Declined"
              description="This upgrade request has been declined. The user will need to submit a new request with updated documentation."
              type="error"
              showIcon
            />
          )}
        </div>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 0' }}>
          <Text type="secondary">No request selected</Text>
        </div>
      )}
    </Drawer>
  );
};

export default UserRequestDetailDrawer;
